import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  AlertTriangle,
  Camera,
  CheckCircle2,
  Circle,
  Eye,
  MinusCircle,
  Upload,
  UserRound,
} from "lucide-react"
import { useEffect, useState } from "react"

import { api, ErrorApi, pedir, pedirBinario } from "@/api/cliente"
import { BotonArchivo } from "@/componentes/ui/BotonArchivo"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { type ArchivoParaVer, VistaPreviaArchivo } from "@/componentes/ui/VistaPreviaArchivo"
import { comoFecha } from "@/lib/fechas"

type Documento = { id: number; nombre_original: string; creado_en: string }
type Item = {
  tipo: string
  seccion: string
  nombre: string
  ayuda: string
  estado: "CARGADO" | "NO_APLICA" | "PENDIENTE"
  documentos: Documento[]
  motivo: string | null
}
export type MiExpediente = {
  ficha: {
    nombres: string
    apellidos: string
    tipo_documento: string
    documento: string
    rif: string | null
    cargo: string | null
    fecha_ingreso: string | null
    fecha_nacimiento: string | null
    telefono: string | null
    email_personal: string | null
    email_corporativo: string | null
    direccion: string | null
    contacto_emergencia: string | null
    telefono_emergencia: string | null
    tiene_foto: boolean
    estado: string
  }
  items: Item[]
  pendientes: string[]
}

export const CLAVE_MI_EXPEDIENTE = ["mi-expediente"]

const ACEPTA = "application/pdf,image/png,image/jpeg,image/webp,.doc,.docx"

/** El retrato propio, por su ruta autenticada (regla 8). */
function MiRetrato({ hay }: { hay: boolean }) {
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!hay) return
    let vivo = true
    let creada: string | null = null
    pedirBinario("/api/v1/mi-expediente/foto")
      .then((b) => {
        creada = URL.createObjectURL(b)
        if (vivo) setUrl(creada)
      })
      .catch(() => undefined)
    return () => {
      vivo = false
      if (creada) URL.revokeObjectURL(creada)
    }
  }, [hay])
  return (
    <div
      className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-full"
      style={{ background: "var(--border)" }}
    >
      {url ? (
        <img src={url} alt="Tu foto" className="size-full object-cover" />
      ) : (
        <UserRound size={36} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
      )}
    </div>
  )
}

/**
 * «Mi ficha» (23/10): el expediente laboral PROPIO, desde el icono de perfil del
 * encabezado. Ver `api/v1/mi_expediente.py`.
 *
 * ⚠ **Sólo lo suyo**: el servidor resuelve la ficha por la sesión; aquí no hay id.
 * ⚠ **Las alertas son de lo que le toca entregar a ÉL**: lo que tramita la empresa
 * (contrato firmado, IVSS, FAOV…) no se le pide.
 */
export function MiFicha() {
  const cliente = useQueryClient()
  const [viendo, setViendo] = useState<ArchivoParaVer | null>(null)
  const consulta = useQuery({
    queryKey: CLAVE_MI_EXPEDIENTE,
    queryFn: () => api.get<MiExpediente>("/api/v1/mi-expediente"),
    retry: false,
  })
  const refrescar = () => cliente.invalidateQueries({ queryKey: CLAVE_MI_EXPEDIENTE })
  const subir = useMutation({
    mutationFn: ({ tipo, archivo }: { tipo: string; archivo: File }) => {
      const datos = new FormData()
      if (tipo === "FOTO") {
        datos.append("archivo", archivo)
        return pedir("/api/v1/mi-expediente/foto", { method: "POST", body: datos })
      }
      datos.append("tipo", tipo)
      datos.append("archivo", archivo)
      return pedir("/api/v1/mi-expediente/documentos", { method: "POST", body: datos })
    },
    meta: { exito: "Documento cargado" },
    onSuccess: refrescar,
  })

  if (consulta.isPending) return <EstadoCarga etiqueta="Cargando tu ficha…" />
  if (consulta.isError) {
    if (consulta.error instanceof ErrorApi && consulta.error.estado === 404) {
      return (
        <div className="tarjeta mx-auto max-w-lg space-y-2 p-6 text-center">
          <UserRound
            size={36}
            aria-hidden={true}
            className="mx-auto"
            style={{ color: "var(--fg-muted)" }}
          />
          <h1 className="text-lg font-semibold">Tu cuenta aún no tiene ficha laboral</h1>
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Talento Humano tiene que enlazar tu ficha a tu usuario. Cuando lo haga, aquí verás tus
            datos y los documentos que te falta entregar.
          </p>
        </div>
      )
    }
    return <EstadoError titulo="No se pudo abrir tu ficha" error={consulta.error} />
  }
  const { ficha: f, items, pendientes } = consulta.data
  const datos: [string, string | null][] = [
    ["Cédula", `${f.tipo_documento}-${f.documento}`],
    ["RIF", f.rif],
    ["Cargo", f.cargo],
    ["Ingreso", f.fecha_ingreso ? comoFecha(f.fecha_ingreso) : null],
    ["Nacimiento", f.fecha_nacimiento ? comoFecha(f.fecha_nacimiento) : null],
    ["Teléfono", f.telefono],
    ["Correo personal", f.email_personal],
    ["Correo corporativo", f.email_corporativo],
    ["Dirección", f.direccion],
    [
      "Contacto de emergencia",
      [f.contacto_emergencia, f.telefono_emergencia].filter(Boolean).join(" · ") || null,
    ],
  ]

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center gap-4">
        <MiRetrato hay={f.tiene_foto} />
        <div className="min-w-0">
          <h1 className="text-xl font-semibold">
            {f.nombres} {f.apellidos}
          </h1>
          <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
            Mi ficha laboral{f.cargo ? ` · ${f.cargo}` : ""}
          </p>
        </div>
      </header>

      {pendientes.length > 0 ? (
        <section
          className="tarjeta space-y-2 p-4"
          style={{ borderColor: "var(--warning)" }}
          aria-label="Documentos por cargar"
        >
          <p className="flex items-center gap-2 font-medium">
            <AlertTriangle size={18} aria-hidden={true} style={{ color: "var(--warning)" }} />
            Te falta cargar {pendientes.length} documento{pendientes.length === 1 ? "" : "s"}
          </p>
          <ul className="list-inside list-disc text-sm">
            {pendientes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </section>
      ) : (
        <p className="tarjeta flex items-center gap-2 p-4 text-sm">
          <CheckCircle2 size={18} aria-hidden={true} style={{ color: "var(--success)" }} />
          Tienes todos tus documentos al día.
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="tarjeta space-y-2 p-4" aria-label="Mis datos">
          <h2 className="text-base font-semibold">Mis datos</h2>
          <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
            {datos.map(([k, v]) => (
              <div key={k}>
                <dt className="text-xs" style={{ color: "var(--fg-muted)" }}>
                  {k}
                </dt>
                <dd>{v || "—"}</dd>
              </div>
            ))}
          </dl>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            ¿Algún dato está mal? Pídele a Talento Humano que lo corrija.
          </p>
        </section>

        <section className="space-y-2" aria-label="Mis documentos">
          <h2 className="text-base font-semibold">Mis documentos</h2>
          {subir.error instanceof ErrorApi && (
            <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
              {subir.error.message}
            </p>
          )}
          <ul className="tarjeta divide-y" style={{ borderColor: "var(--border)" }}>
            {items.map((i) => (
              <li key={i.tipo} className="flex flex-wrap items-start gap-3 p-3 text-sm">
                {i.estado === "CARGADO" ? (
                  <CheckCircle2
                    size={18}
                    aria-label="Cargado"
                    style={{ color: "var(--success)" }}
                  />
                ) : i.estado === "NO_APLICA" ? (
                  <MinusCircle
                    size={18}
                    aria-label="No aplica"
                    style={{ color: "var(--fg-muted)" }}
                  />
                ) : (
                  <Circle size={18} aria-label="Pendiente" style={{ color: "var(--warning)" }} />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{i.nombre}</p>
                  <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    {i.estado === "NO_APLICA"
                      ? `No aplica${i.motivo ? `: ${i.motivo}` : ""}`
                      : i.ayuda}
                  </p>
                  {i.documentos.map((d) => (
                    <button
                      key={d.id}
                      type="button"
                      className="mt-1 flex items-center gap-1 text-xs underline-offset-2 hover:underline"
                      onClick={() =>
                        setViendo({
                          ruta: `/api/v1/mi-expediente/documentos/${d.id}/archivo`,
                          nombre: d.nombre_original,
                        })
                      }
                    >
                      <Eye size={12} aria-hidden={true} />
                      {d.nombre_original} · {comoFecha(d.creado_en)}
                    </button>
                  ))}
                </div>
                {i.estado !== "NO_APLICA" && (
                  <BotonArchivo
                    accept={i.tipo === "FOTO" ? "image/png,image/jpeg,image/webp" : ACEPTA}
                    etiqueta={`Cargar ${i.nombre}`}
                    disabled={subir.isPending}
                    onArchivo={(archivo) => subir.mutate({ tipo: i.tipo, archivo })}
                  >
                    {i.tipo === "FOTO" ? (
                      <Camera size={14} aria-hidden={true} />
                    ) : (
                      <Upload size={14} aria-hidden={true} />
                    )}
                    {i.estado === "CARGADO"
                      ? i.tipo === "FOTO"
                        ? "Cambiar"
                        : "Añadir otro"
                      : "Cargar"}
                  </BotonArchivo>
                )}
              </li>
            ))}
          </ul>
        </section>
      </div>
      <VistaPreviaArchivo archivo={viendo} onCerrar={() => setViendo(null)} />
    </div>
  )
}
